import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import { SMS_CONSENT_TEXT } from '../components/ui/SmsConsentCheckbox';
import ContactRequestForm from '../components/ui/ContactRequestForm';
import useReveal from '../components/ui/Reveal';
import COMPANY, { ROUTES } from '../config/company';

/** Demo SMS opt-in page reusing the Contact page form UI (no backend). */

const B = COMPANY.brand;

// Copy these into the Telnyx campaign exactly. Customer care first, then marketing.
export const SAMPLE_MESSAGES = [
  `${B}: Thanks for your dry van freight inquiry. Our team can follow up about the lane and requirements. Reply STOP to opt out or HELP for help.`,
  `${B}: We received your freight-service inquiry. Reply HELP for assistance, STOP to opt out.`,
  `${B}: Your freight inquiry has an update. Please review the details sent to your email. Reply STOP to opt out, HELP for help.`,
  `${B}: Need assistance with a trucking or freight inquiry? Email ${COMPANY.emailDisplay}. Reply STOP to opt out, HELP for help.`,
];
export const MARKETING_SAMPLES = [
  `${B}: Want information about our dry van freight services? Email ${COMPANY.emailDisplay} for details. Reply STOP to opt out, HELP for help.`,
  `${B}: Ask about refrigerated and flatbed trucking services at ${COMPANY.emailDisplay}. Reply STOP to opt out, HELP for help.`,
];
export const OPT_IN_CONFIRMATION = `${B}: Thanks for subscribing to SMS updates. Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.`;
export const HELP_REPLY = `${B}: For help call ${COMPANY.phoneDisplay}, email ${COMPANY.emailDisplay}, or visit ${COMPANY.website}/contact. Reply STOP to opt out.`;
export const STOP_REPLY = `${B}: You are unsubscribed and will receive no further SMS messages.`;

export const PROGRAM_TERMS = [
  ['Program name', COMPANY.sms.programName],
  ['Customer care messages', 'Customer care replies, freight quote follow-ups, shipment and scheduling updates, dispatch coordination, and account or billing notifications about a trucking or freight inquiry.'],
  ['Marketing messages', `Freight-service information, trucking-related offers and company updates. Sent only if you check the separate marketing box. Up to 4 marketing messages per month.`],
  ['Frequency', `${COMPANY.sms.frequency}.`],
  ['Cost', `Message and data rates may apply. ${B} does not charge for text messages.`],
  ['Opt out', 'Reply STOP to any message to unsubscribe at any time. You will receive one final confirmation and no further messages.'],
  ['Help', `Reply HELP to any message, call ${COMPANY.phoneDisplay}, or email ${COMPANY.emailDisplay}.`],
  ['Privacy', 'Your mobile phone number and SMS consent will not be sold or shared with third parties or affiliates for promotional or marketing purposes.'],
];

export default function SmsOptIn() {
  useReveal();

  return (
    <>
      <PageHero
        eyebrow="SMS opt-in"
        title="Bitnex Freight LLC SMS updates"
        lede="Choose whether you want text-message updates from Bitnex Freight LLC about trucking inquiries and freight-service offers. Consent is optional and not required to request services."
        compact
      />

      <section className="py-16 sm:py-20">
        <div className="shell grid gap-10 lg:grid-cols-[1.1fr_1fr]">
          <ContactRequestForm mode="sms-opt-in" />

          <div className="space-y-5">
            <div className="card reveal texture-dark bg-navy-900 p-7 text-white">
              <div className="eyebrow text-gold-300">How to opt in</div>
              <h2 className="mt-3 font-display text-2xl font-bold text-white">Only you can choose to receive texts.</h2>
              <ol className="mt-4 list-decimal space-y-2 pl-5 text-[0.9rem] leading-relaxed text-navy-100">
                <li>Enter your name and mobile phone number in the form on this page (or on our <Link className="underline text-gold-300" to={ROUTES.contact}>Contact form</Link>).</li>
                <li>Check the optional customer care box and/or the optional marketing box. Neither box is pre-checked.</li>
                <li>Select Send message to submit your request. A visual confirmation appears on the page. This demo does not transmit or save your selections.</li>
                <li>Example SMS program confirmation (sent only after SMS service setup): <em>"{OPT_IN_CONFIRMATION}"</em></li>
              </ol>
              <p className="mt-4 text-[0.85rem] leading-relaxed text-navy-100">
                Reply <strong>STOP</strong> to any message to opt out, or <strong>HELP</strong> for help. Message and data rates may apply. Consent is not a condition of purchase.
              </p>
            </div>
            <div className="card reveal p-7">
              <div className="eyebrow mb-2">Program summary</div>
              <dl className="space-y-3 text-[0.9rem]">
                {PROGRAM_TERMS.map(([k, v]) => (
                  <div key={k}>
                    <dt className="font-bold text-navy-900">{k}</dt>
                    <dd className="text-ink-600">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-5 text-[0.85rem] text-ink-600">
                Full details: <Link className="font-semibold underline" to={ROUTES.smsTerms}>SMS Terms</Link>,{' '}
                <Link className="font-semibold underline" to={ROUTES.privacy}>Privacy Policy</Link> and{' '}
                <Link className="font-semibold underline" to={ROUTES.terms}>Terms &amp; Conditions</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink-50 py-16 sm:py-20" id="consent-language">
        <div className="shell">
          <div className="eyebrow mb-3">Consent language</div>
          <h2 className="h2">The exact wording you agree to.</h2>
          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            <div className="card p-6">
              <div className="text-[0.72rem] font-bold uppercase tracking-[0.16em] text-gold-600">Customer care checkbox</div>
              <p className="mt-3 text-[0.9rem] leading-relaxed text-ink-700">{SMS_CONSENT_TEXT.customerCare}</p>
            </div>
            <div className="card p-6">
              <div className="text-[0.72rem] font-bold uppercase tracking-[0.16em] text-gold-600">Marketing checkbox</div>
              <p className="mt-3 text-[0.9rem] leading-relaxed text-ink-700">{SMS_CONSENT_TEXT.marketing}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="shell grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <div className="eyebrow mb-3">Message examples</div>
            <h2 className="h2">Texts you may receive.</h2>
            <p className="mt-4 text-ink-600">Customer care messages go only to people who select customer-care consent. Promotional messages go only to people who select marketing consent. Reply STOP to opt out at any time.</p>
          </div>
          <div className="space-y-3">
            {[
              ['Confirmation', OPT_IN_CONFIRMATION],
              ...SAMPLE_MESSAGES.map((m, i) => [`Customer care ${i + 1}`, m]),
              ...MARKETING_SAMPLES.map((m, i) => [`Marketing ${i + 1}`, m]),
              ['HELP reply', HELP_REPLY],
              ['STOP reply', STOP_REPLY],
            ].map(([label, msg]) => (
              <div key={label} className="reveal flex gap-4">
                <div className="w-28 shrink-0 pt-3 text-right text-[0.72rem] font-bold uppercase tracking-wider text-ink-500">{label}</div>
                <div className="rounded-2xl rounded-tl-sm bg-ink-100 px-5 py-3.5 text-[0.9rem] leading-relaxed text-ink-800">{msg}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
